import React from 'react'
import { useTranslation } from 'react-i18next'

import { useFormState, useFormErrorState } from '~/common/form/formCommon'
import { CREATE_SIGNUP } from '~/api/signup'

import { SignupType, initialData, signupValidation } from '~/components/app/form/signup/signupType'

import { agreementPath } from '~/common/define/path'
import { OperatingCompanyUrl } from '~/common/define/url'
import Form from '~/components/app/form/base/Form'
import FormRow from '~/components/wrapper/form/FormRow'
import FormLabel from '~/components/wrapper/form/FormLabel'
import FormInput from '~/components/wrapper/form/FormInput'
import FormTextBox from '~/components/molecules/form/textbox/FormTextBox'
import FormPassword from '~/components/molecules/form/textbox/FormPassword'
import ActionButton from '~/components/atoms/button/ActionButton'
import { ButtonType } from '~/components/atoms/button/ButtonBase'
import ExternalLink from '~/components/atoms/link/ExternalLink'
import FormEmailBox from '~/components/molecules/form/textbox/FormEmailBox'
import SupplementText from '~/components/atoms/text/SupplementText'
import FormCenterArea from '~/components/wrapper/form/FormCenterArea'
import FormInputDescription from '~/components/wrapper/form/FormInputDescription'

interface Props {
  completed(): void
}

const SignupForm = ({completed}: Props) => {
  const { t } = useTranslation()
  const [formData, setData] = useFormState<SignupType>(initialData)
  const [formError, setError] = useFormErrorState()

  const complete = () => {
    completed()
  }

  return (
    <Form
      mutation={CREATE_SIGNUP}
      complete={complete}
      formData={formData}
      formError={formError}
      setData={setData}
      setError={setError}
      validations={signupValidation}
    >
      <FormRow>
        <FormLabel require>
          {t('org:code')}
        </FormLabel>
        <FormInput>
          <FormTextBox
            required
            name="organization_code"
            label={t('org:code')}
            value={formData.organization_code}
            onChange={setData}
            errors={formError.formWidgetErrors.organization_code}
            onError={setError}
            placeholder={t('org:placeholder.code')}
          />
          <FormInputDescription>
            {t('annotation.code')}<br/>
            {t('org:annotation.code')}
          </FormInputDescription>
        </FormInput>
      </FormRow>
      <FormRow>
        <FormLabel require>
          {t('org:name')}
        </FormLabel>
        <FormInput>
          <FormTextBox
            required
            name="organization_name"
            label={t('org:name')}
            value={formData.organization_name}
            onChange={setData}
            errors={formError.formWidgetErrors.organization_name}
            onError={setError}
            placeholder={t('org:placeholder.name')}
          />
        </FormInput>
      </FormRow>
      <FormRow>
        <FormLabel require>
          {t('account:name')}
        </FormLabel>
        <FormInput>
          <FormTextBox
            required
            name="user_name"
            label={t('account:name')}
            value={formData.user_name}
            onChange={setData}
            errors={formError.formWidgetErrors.user_name}
            onError={setError}
            placeholder={t('account:placeholder.name')}
          />
        </FormInput>
      </FormRow>
      <FormRow>
        <FormLabel require>
          {t('email_address')}
        </FormLabel>
        <FormInput>
          <FormEmailBox
            required
            name="email"
            label={t('email_address')}
            value={formData.email}
            onChange={setData}
            errors={formError.formWidgetErrors.email}
            onError={setError}
            placeholder={t('placeholder.email')}
          />
        </FormInput>
      </FormRow>
      <FormRow>
        <FormLabel require>
          {t('password')}
        </FormLabel>
        <FormInput>
          <FormPassword
            required
            name="password"
            label={t('password')}
            value={formData.password}
            onChange={setData}
            errors={formError.formWidgetErrors.password}
            onError={setError}
            isSetting
          />
        </FormInput>
      </FormRow>
      <FormCenterArea>
        <p>「無料で利用開始」を押下することで、VISLITEの
          <ExternalLink mark link={agreementPath()}>
            利用規約
          </ExternalLink>と<br/>
          <ExternalLink mark link={OperatingCompanyUrl.PRIVACY}>
            プライバシーポリシー
          </ExternalLink>に同意したことになります
        </p>
        <ActionButton type={ButtonType.SUBMIT}>
          {t('signup:create')}
        </ActionButton>
        <SupplementText weak>
          {t('signup:annotation')}
        </SupplementText>
      </FormCenterArea>
    </Form>
  )
}

export default SignupForm
